import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { doUploadImage } from '../store/image.slice';
import { doRemoveAuth } from '../store/auth.slice';
import { Editor } from '@tinymce/tinymce-react';
import { useSnackbar } from 'notistack';
import { errorVariant, warningVariant } from '../lib/barVariant';
import useNotification from "../hooks/useNotification";

interface IProps {
  content: string;
  setContent: (content: string) => void;
  charLimit?: number;
}


const Tinymce: FC<IProps> = ({
  content,
  setContent,
  charLimit = 2000,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { warningNotification } = useNotification();
  const history = useHistory();
  const dispatch = useDispatch();
  let url = '';
  
  const encodeWhiteSpaces = (str: string) => {
    let space = str?.substr(0, 5);
    if (space === '     ') {
      space = '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;';
      str = str.substr(5);
      str = space + str;
    }

    if(str?.includes('\n')){
      str = str.replace(/\n/g, '<br>')
    }
    return str;
  }

  const onUploadImage = async (file: File) => {
    const form = new FormData();
    form.append('img', file);

    const res = await dispatch(doUploadImage(form));

    switch (res?.status) {
      case 200:
        return res.data;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }

    return '';
  }

  const charCount = (value: string) => {
    return (value ?? '')?.replace(/(<[^>]*>|&nbsp)?/g, '').length ?? 0
  };

  return (
    <>
      <Editor
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        value={content}
        onEditorChange={(value: string, editor: any) => {
          if (charCount(value) > charLimit) {
            return warningNotification(t('common.message.overWrite'));
          }
          setContent(value);
        }}
        init={{
          apply_source_formatting: true,
          body_class: 'test',
          branding: false,
          menubar: false,
          statusbar: false,
          plugins: [
            'advlist autolink lists link image charmap print preview anchor',
            'searchreplace visualblocks code fullscreen',
            'insertdatetime media table paste code help wordcount',
          ],
          toolbar:
            'undo redo | bold italic forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | image code',
          content_style:
            'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
          forced_root_block: 'div',
          extended_valid_elements: 'a[style|href|onclick]',
          // image upload
          automatic_uploads: false,
          image_advtab: false,
          file_browser_callback_types: 'image',
          file_picker_callback: (callback, value, meta) => {
            if (meta.filetype === 'image') {
              const input: any = document.getElementById('imageInput');
              if (!input) return;
              //if (url) callback(url);

              input.click();
              input.onchange = async () => {
                const file = input.files[0];
                if (!file) return;
                url = await onUploadImage(file);
                callback(url);
              };
            }
          },
        }}
      />
      <input id="imageInput" type="file" style={{ display: 'none' }} />
    </>
  );
}

export default Tinymce;
